// Textos de "Minhas assinaturas" (specs/008-minhas-assinaturas/contracts/ui-contract.md §Textos fixos).
export const subscriptionsCopy = {
  title: 'Minhas assinaturas',
  back: 'Minhas assinaturas',
  history: (count: number) => `Histórico (${count})`,
  dependent: (name: string) => `Assinatura de ${name}`,
  cycleEvery: (days: number) => `a cada ${days} dias`,
  cardLabel: (brand?: string | null, lastDigits?: string | null) =>
    `${brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Cartão'} final ${lastDigits ?? '----'}`,
  noCard: 'Nenhum cartão',
  moreItems: (count: number) => `+${count}`,
  pauseEnding: (date: string) => `Sua assinatura será encerrada em ${date} se não for retomada`,

  empty: {
    title: 'Você ainda não tem assinaturas',
    benefit: 'Receba seus produtos automaticamente, com desconto de assinante em todo ciclo.',
    cta: 'Ver produtos com assinatura'
  },
  loadError: {
    list: 'Não foi possível carregar suas assinaturas',
    detail: 'Não foi possível carregar esta assinatura',
    retry: 'Tentar de novo'
  },
  notFound: 'Assinatura não encontrada',

  sections: {
    items: 'Itens',
    deliveryPayment: 'Entrega e pagamento',
    documents: 'Documentos',
    cycles: 'Ciclos',
    history: 'Histórico'
  },

  buttons: {
    details: 'Ver detalhes',
    skip: 'Pular',
    postpone: 'Adiar',
    pause: 'Pausar',
    resume: 'Retomar',
    cancel: 'Cancelar assinatura',
    changeCard: 'Trocar cartão',
    edit: 'Editar',
    save: 'Salvar alterações',
    discard: 'Descartar',
    addItem: 'Incluir produto',
    remove: 'Remover',
    retry: 'Tentar de novo',
    viewOrder: 'Ver pedido',
    back: 'Voltar',
    sendDocuments: 'Enviar documentos',
    viewDocuments: 'Ver documentos',
    seeMore: 'Ver mais'
  },

  header: {
    nextCharge: 'Próxima cobrança',
    products: 'Produtos',
    discountNote: 'Preços já com desconto de assinante',
    shipping: 'Frete',
    shippingUnknown: 'Calculado perto da cobrança',
    total: 'Total estimado',
    editUntil: (date: string) => `Editar até ${date}`,
    cycleLocked: 'Este ciclo já está em processamento; alterações valem para o próximo'
  },

  items: {
    quantity: (q: number) => `Qtd. ${q}`,
    withDiscount: 'com desconto de assinante',
    unavailable: 'Temporariamente indisponível',
    unavailableHelp: 'Este item não será enviado enquanto estiver indisponível; o valor dele não é cobrado.',
    cycleLabel: 'Receber a cada',
    previewTitle: 'Como fica a próxima cobrança',
    saveOrDiscardFirst: 'Salve ou descarte as alterações antes',
    aboveAllowed: (max: number) => `Sua receita permite até ${max} por ciclo`,
    sendNewPrescription: 'Enviar receita nova',
    maxReached: (max: number) => `Máximo de ${max} por item`
  },

  delivery: {
    address: 'Endereço de entrega',
    noAddress: 'Endereço não informado',
    addressHelp: 'Para mudar o endereço, fale com o acolhimento',
    talkToUs: 'Falar com o acolhimento',
    card: 'Cartão da assinatura'
  },

  documents: {
    prescription: 'Receita',
    anvisa: 'Autorização Anvisa',
    valid: (date: string) => `Válida até ${date}`,
    expiringSoon: (days: number) => (days <= 0 ? 'vence hoje' : `vence em ${days} ${days === 1 ? 'dia' : 'dias'}`),
    expired: (date: string) => `Vencida em ${date}`,
    pendingReview: 'Em análise pelo acolhimento',
    none: 'Não enviada',
    loadError: 'Não foi possível carregar seus documentos agora',
    schedule: 'Agendar consulta de renovação',
    sendPrescription: 'Enviar nova receita',
    sendAnvisa: 'Enviar autorização Anvisa',
    anvisaHowTo: 'Como renovar sua autorização Anvisa',
    prescriptionSent: 'Em análise pelo acolhimento. Assim que for aprovada, sua assinatura volta sozinha.',
    anvisaSent: 'Recebemos sua autorização. O acolhimento vai conferir.',
    dependent: (name: string) => `Para enviar o documento de ${name}, fale com o acolhimento`,
    reasonsTitle: 'O que falta para retomar'
  },

  upload: {
    titlePrescription: 'Enviar nova receita',
    titleAnvisa: 'Enviar autorização Anvisa',
    description: 'PDF, JPG ou PNG de até 2 MB.',
    chooseFile: 'Escolher arquivo',
    emission: 'Data de emissão',
    emissionRequired: 'Informe a data de emissão',
    emissionFuture: 'A data de emissão não pode ser futura',
    fileRequired: 'Escolha um arquivo',
    tooBig: 'O arquivo precisa ter até 2 MB',
    badFormat: 'Envie um arquivo PDF, JPG ou PNG',
    submit: 'Enviar',
    success: 'Documento enviado'
  },

  cycles: {
    cycle: (n: number) => `Ciclo ${n}`,
    scheduled: 'Data prevista',
    notified: 'Valor avisado',
    charged: 'Valor cobrado',
    attempts: (n: number) => `${n} tentativas de pagamento`,
    current: 'Ciclo atual',
    empty: 'Nenhum ciclo ainda'
  },

  historyList: {
    empty: 'Nenhuma alteração ainda'
  },

  alerts: {
    payment: 'Não conseguimos cobrar seu cartão. Troque o cartão para a assinatura voltar.',
    documents: 'Há documentos vencidos ou perto de vencer nesta assinatura.',
    pendingTitle: 'Aguardando documentos',
    pending: (what: string, date?: string) =>
      `Recebemos seu pagamento. Para garantir os próximos envios, envie ${what}${date ? ` até ${date}` : ''}.`,
    pendingWhat: (docs: Array<'prescription' | 'anvisa'>) => {
      const hasPrescription = docs.includes('prescription')
      const hasAnvisa = docs.includes('anvisa')
      if (hasPrescription && hasAnvisa) return 'sua receita e autorização Anvisa'
      if (hasAnvisa) return 'sua autorização Anvisa'
      return 'sua receita'
    }
  },

  skip: {
    title: 'Pular próximo envio',
    body: (from: string, next: string) => `O próximo envio de ${from} será pulado. Nova cobrança em ${next}.`,
    bodyNoNext: (from: string) => `O próximo envio de ${from} será pulado.`,
    confirm: 'Pular envio'
  },
  postpone: {
    title: 'Adiar próxima cobrança',
    field: 'Nova data da cobrança',
    once: 'Só é possível adiar uma vez por ciclo',
    confirm: 'Adiar cobrança',
    required: 'Escolha uma data',
    range: (min: string, max: string) => `Escolha uma data entre ${min} e ${max}`
  },
  pause: {
    title: 'Pausar assinatura',
    field: 'Pausar até',
    help: 'Depois de 90 dias pausada, a assinatura é encerrada. Ela volta sozinha na data escolhida.',
    confirm: 'Pausar assinatura',
    required: 'Escolha uma data',
    range: (min: string, max: string) => `Escolha uma data entre ${min} e ${max}`
  },
  cancel: {
    title: 'Cancelar assinatura',
    retention: 'Prefere pausar ou adiar?',
    reason: 'Motivo (opcional)',
    reasonPlaceholder: 'Selecione um motivo',
    comment: 'Comentário (opcional)',
    confirm: 'Confirmar cancelamento',
    remaining: (n: number) => `${n} caracteres restantes`
  },

  card: {
    title: 'Trocar cartão',
    help: 'Para usar outro cartão, faça uma compra com ele e depois volte aqui.',
    notEligible: 'Use um cartão que já tenha uma compra aprovada na Cannect',
    resumed: 'Cartão trocado. Sua assinatura voltou e será cobrada amanhã.',
    confirm: 'Usar este cartão'
  },

  addItem: {
    title: 'Incluir produto',
    search: 'Buscar produto',
    searchPlaceholder: 'Digite ao menos 3 letras',
    noResults: 'Nenhum produto encontrado',
    alreadyIn: 'Já está na assinatura',
    notPrescribed: 'Este produto não está na sua receita. Fale com o acolhimento',
    confirm: 'Incluir',
    quantity: 'Quantidade'
  },
  removeItem: {
    title: (name: string) => `Remover ${name}`,
    lastItem: 'Sua assinatura precisa de pelo menos um produto.',
    lastItemHelp: 'Para parar de receber, cancele a assinatura.',
    confirm: 'Remover produto'
  },

  errors: {
    lockBusy: 'Estamos processando outra alteração, tente em alguns segundos',
    partial: 'Parte da alteração não foi concluída; o acolhimento foi avisado.'
  },

  preview: {
    newTotal: 'Novo total estimado',
    nextCharge: 'Próxima cobrança'
  }
}

export type SubscriptionsCopy = typeof subscriptionsCopy
